/**
 * List View Table Component
 *
 * Displays records in a table format for list views.
 * Matches dark navy design system from dashboard.
 */

import React from 'react';
import { ChevronRight, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import type { ListView, ListViewRecord } from '../../types/listView';
import { formatDateTime, formatDate } from '../../services/queries/clientListViewQueries';
import { resolveFieldValue } from '../../utils/fieldValueResolver';
import { LookupFieldCell } from './LookupFieldCell';

interface ListViewTableProps {
  listView: ListView;
  records: ListViewRecord[];
  onRecordClick: (record: ListViewRecord) => void;
  isLoading?: boolean;
  sortConfig?: 
    | { columns: { field: string; direction: 'asc' | 'desc' }[] }
    | { field: string; direction: 'asc' | 'desc' }
    | null;
  onSort?: (field: string, event?: React.MouseEvent) => void | ((config: { field: string; direction: 'asc' | 'desc' } | null) => void);
  selectedRecordIds?: Set<string>;
  onSelectRecord?: (recordId: string, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
}

/**
 * Status Badge Component
 */
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors: Record<string, string> = {
    // Client/Contact statuses
    Active: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 border-green-300 dark:border-green-500/30',
    Inactive: 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-400 border-gray-300 dark:border-gray-500/30',
    Pending: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-300 dark:border-yellow-500/30',

    // Lead statuses
    New: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-500/30',
    Contacted: 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-500/30',
    Qualified: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 border-green-300 dark:border-green-500/30',
    Unqualified: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-red-300 dark:border-red-500/30',

    // Lead ratings
    Hot: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-red-300 dark:border-red-500/30',
    Warm: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-400 border-orange-300 dark:border-orange-500/30',
    Cold: 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-400 border-gray-300 dark:border-gray-500/30',

    // Opportunity stages
    'Meeting Set': 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-400 border-gray-300 dark:border-gray-500/30',
    'Discovery': 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-500/30',
    'Pitch': 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-500/30',
    'Evaluation': 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-400 border-orange-300 dark:border-orange-500/30',
    'Pending Deployment': 'bg-teal-100 dark:bg-teal-500/20 text-teal-700 dark:text-teal-400 border-teal-300 dark:border-teal-500/30',
    'Closed Won': 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 border-green-300 dark:border-green-500/30',
    'Closed Lost': 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-red-300 dark:border-red-500/30',

    // Case statuses
    'In Progress': 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-500/30',
    'Waiting on Customer': 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-300 dark:border-yellow-500/30',
    'Waiting on Engineering': 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-500/30',
    Escalated: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-red-300 dark:border-red-500/30',
    Resolved: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 border-green-300 dark:border-green-500/30',
    Closed: 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-400 border-gray-300 dark:border-gray-500/30',

    // Case priorities
    Critical: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-red-300 dark:border-red-500/30',
    High: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-400 border-orange-300 dark:border-orange-500/30',
    Medium: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-300 dark:border-yellow-500/30',
    Low: 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-400 border-gray-300 dark:border-gray-500/30',

    // Event types
    Meeting: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-500/30',
    Call: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 border-green-300 dark:border-green-500/30',
    Email: 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-500/30',
    Task: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-400 border-orange-300 dark:border-orange-500/30',
    Deadline: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-red-300 dark:border-red-500/30',
    Conference: 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 border-indigo-300 dark:border-indigo-500/30',
    Training: 'bg-cyan-100 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-400 border-cyan-300 dark:border-cyan-500/30',
    Demo: 'bg-pink-100 dark:bg-pink-500/20 text-pink-700 dark:text-pink-400 border-pink-300 dark:border-pink-500/30',

    // Event statuses
    Scheduled: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-500/30',
    Confirmed: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 border-green-300 dark:border-green-500/30',
    Tentative: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-300 dark:border-yellow-500/30',
    Cancelled: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-red-300 dark:border-red-500/30',
    Completed: 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-400 border-gray-300 dark:border-gray-500/30',
    'No Show': 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-red-300 dark:border-red-500/30',

    // Report types
    Sales: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-500/30',
    Service: 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-500/30',
    Marketing: 'bg-pink-100 dark:bg-pink-500/20 text-pink-700 dark:text-pink-400 border-pink-300 dark:border-pink-500/30',
    Finance: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 border-green-300 dark:border-green-500/30',
    Operations: 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 border-indigo-300 dark:border-indigo-500/30',
    Custom: 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-400 border-gray-300 dark:border-gray-500/30',

    // Report statuses
    // Active: already defined above in Client/Contact statuses
    // Scheduled: already defined above in Event statuses
    Draft: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-300 dark:border-yellow-500/30',
    Archived: 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-400 border-gray-300 dark:border-gray-500/30',
  };

  return (
    <span className={`px-2 py-1 text-xs rounded-full border ${colors[status] || colors.Inactive}`}>
      {status}
    </span>
  );
};

/**
 * Format cell value based on field type
 */
const formatCellValue = (value: any, fieldType: string, lookupObject?: string, recordId?: string, fieldApiName?: string): React.ReactNode => {
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400 dark:text-gray-500">—</span>;
  }

  // Check if this is a lookup field (by type or by lookupObject presence)
  const isLookupField = fieldType === 'lookup' || fieldType === 'masterdetail' || !!lookupObject;
  
  // If we have a lookupObject but fieldType isn't set correctly, treat it as lookup
  if (isLookupField && lookupObject) {
    // Render lookup field as link with label
    return (
      <LookupFieldCell
        recordId={String(value)}
        lookupObject={lookupObject}
      />
    );
  }

  switch (fieldType) {
    case 'badge':
      return <StatusBadge status={value} />;

    case 'datetime':
      return <span className="text-gray-700 dark:text-gray-300">{formatDateTime(value)}</span>;

    case 'date':
      return <span className="text-gray-700 dark:text-gray-300">{formatDate(value)}</span>;

    case 'email':
      return (
        <a
          href={`mailto:${value}`}
          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {value}
        </a>
      );

    case 'phone':
      return (
        <a
          href={`tel:${value}`}
          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {value}
        </a>
      );

    case 'boolean':
      return (
        <span className={value ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
          {value ? 'Yes' : 'No'}
        </span>
      );

    case 'number':
      return <span className="text-gray-700 dark:text-gray-300">{Number(value).toLocaleString()}</span>;

    case 'multipicklist':
      // Multi-select values: show comma-separated list (array from API or comma-separated string)
      if (Array.isArray(value)) {
        const joined = value.map((v: any) => (v != null ? String(v) : '')).filter(Boolean).join(', ');
        return joined ? (
          <span className="text-gray-700 dark:text-gray-300">{joined}</span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">—</span>
        );
      }
      if (typeof value === 'string' && value.includes(',')) {
        // Already comma-separated string: normalize spacing
        const normalized = value.split(',').map(s => s.trim()).filter(Boolean).join(', ');
        return normalized ? (
          <span className="text-gray-700 dark:text-gray-300">{normalized}</span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">—</span>
        );
      }
      return <span className="text-gray-700 dark:text-gray-300">{String(value)}</span>;

    case 'picklist':
      // Single-select: value may be string or (if mishandled) array; normalize to string
      if (Array.isArray(value)) {
        const joined = value.map((v: any) => (v != null ? String(v) : '')).filter(Boolean).join(', ');
        return joined ? (
          <span className="text-gray-700 dark:text-gray-300">{joined}</span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">—</span>
        );
      }
      return <span className="text-gray-700 dark:text-gray-300">{String(value)}</span>;

    case 'richtextarea':
      // Render HTML content for rich text fields
      if (typeof value === 'string' && value.trim()) {
        // Strip HTML tags to get plain text for display length check
        const textContent = value.replace(/<[^>]*>/g, '').trim();
        if (!textContent) {
          return <span className="text-gray-400 dark:text-gray-500">—</span>;
        }
        
        return (
          <div
            className="text-sm text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none line-clamp-2"
            dangerouslySetInnerHTML={{ __html: value }}
            onClick={(e) => e.stopPropagation()}
            style={{ 
              maxWidth: '400px', 
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}
          />
        );
      }
      return <span className="text-gray-400 dark:text-gray-500">—</span>;

    default:
      return <span className="text-gray-700 dark:text-gray-300">{value}</span>;
  }
};

export const ListViewTable: React.FC<ListViewTableProps> = ({
  listView,
  records,
  onRecordClick,
  isLoading,
  sortConfig,
  onSort,
  selectedRecordIds = new Set(),
  onSelectRecord,
  onSelectAll
}) => {
  // Get visible columns sorted by order
  const visibleColumns = listView.columns
    .filter(col => col.isVisible)
    .sort((a, b) => a.order - b.order);

  // Handle sort with event for Shift+click detection
  const handleSortClick = (field: string, event: React.MouseEvent) => {
    if (!onSort) return;
    
    // Call onSort with field and optional event
    // All ListView components now use compatible functions
    if (typeof onSort === 'function') {
      (onSort as (field: string, event?: React.MouseEvent) => void)(field, event);
    }
  };

  // Render sort icon with priority indicator
  const renderSortIcon = (field: string) => {
    // Handle new multi-column format
    if (sortConfig && 'columns' in sortConfig && sortConfig.columns) {
      const sortColumn = sortConfig.columns.find(col => col.field === field);
      if (!sortColumn) {
        return <ArrowUpDown className="w-3 h-3 text-gray-400 dark:text-gray-500" />;
      }

      const priority = sortConfig.columns.findIndex(col => col.field === field) + 1;
      const hasMultipleSorts = sortConfig.columns.length > 1;

      return (
        <div className="flex items-center gap-1">
          {sortColumn.direction === 'asc' ? (
            <ArrowUp className="w-3 h-3 text-blue-600 dark:text-blue-400" />
          ) : (
            <ArrowDown className="w-3 h-3 text-blue-600 dark:text-blue-400" />
          )}
          {hasMultipleSorts && (
            <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">{priority}</span>
          )}
        </div>
      );
    }
    
    // Handle old single-column format
    if (sortConfig && 'field' in sortConfig) {
      if (sortConfig.field === field) {
        return sortConfig.direction === 'asc' ? (
          <ArrowUp className="w-3 h-3 text-blue-600 dark:text-blue-400" />
        ) : (
          <ArrowDown className="w-3 h-3 text-blue-600 dark:text-blue-400" />
        );
      }
    }
    
    return <ArrowUpDown className="w-3 h-3 text-gray-400 dark:text-gray-500" />;
  };
  
  // Check if all records are selected
  const allSelected = records.length > 0 && records.every(r => selectedRecordIds.has(r.id));
  const someSelected = records.some(r => selectedRecordIds.has(r.id));

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading records...</p>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
        <div className="flex flex-col items-center justify-center text-center">
          <p className="text-gray-700 dark:text-gray-400 text-lg mb-2">No records found</p>
          <p className="text-gray-500 dark:text-gray-500 text-sm">Try adjusting your filters or create a new record</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          {/* Table Header */}
          <thead className="bg-gray-50 dark:bg-[#0f172a] border-b border-gray-200 dark:border-gray-700">
            <tr>
              {onSelectRecord && (
                <th className="px-6 py-3 w-12">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = someSelected && !allSelected;
                    }}
                    onChange={(e) => onSelectAll?.(e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
              )}
              {visibleColumns.map((column, index) => (
                <th
                  key={`header-${column.fieldApiName}-${index}`}
                  onClick={(e) => handleSortClick(column.fieldApiName, e)}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-[#334155] transition-colors select-none"
                  title={sortConfig && 'columns' in sortConfig && sortConfig.columns.length > 0 ? 'Click to sort, Shift+Click to add secondary sort' : 'Click to sort'}
                >
                  <div className="flex items-center gap-2">
                    <span>{column.fieldLabel}</span>
                    {renderSortIcon(column.fieldApiName)}
                  </div>
                </th>
              ))}
              <th className="px-6 py-3 w-12"></th>
            </tr>
          </thead>

          {/* Table Body */}
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {records.map((record) => (
              <tr
                key={record.id}
                onClick={() => onRecordClick(record)}
                className={`hover:bg-gray-50 dark:hover:bg-[#334155] cursor-pointer transition-colors group ${
                  selectedRecordIds.has(record.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
              >
                {onSelectRecord && (
                  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedRecordIds.has(record.id)}
                      onChange={(e) => onSelectRecord(record.id, e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                  </td>
                )}
                {visibleColumns.map((column, index) => {
                  const fieldValue = resolveFieldValue(record, column.fieldApiName);
                  // Debug logging for lookup fields
                  if (column.fieldApiName.toLowerCase().includes('relationship') || column.fieldApiName.toLowerCase().includes('lookup')) {
                    console.log(`[ListViewTable] Column:`, {
                      fieldApiName: column.fieldApiName,
                      fieldType: column.fieldType,
                      lookupObject: column.lookupObject,
                      fieldValue
                    });
                  }
                  return (
                    <td 
                      key={`cell-${column.fieldApiName}-${index}`} 
                      className={`px-6 py-4 ${column.fieldType === 'richtextarea' ? '' : 'whitespace-nowrap'}`}
                    >
                      {column.order === 0 ? (
                        // First column is name - make it prominent
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatCellValue(fieldValue, column.fieldType, column.lookupObject, record.id, column.fieldApiName)}
                        </div>
                      ) : (
                        <div className="text-sm">
                          {formatCellValue(fieldValue, column.fieldType, column.lookupObject, record.id, column.fieldApiName)}
                        </div>
                      )}
                    </td>
                  );
                })}
                <td className="px-6 py-4">
                  <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Table Footer - Record Count */}
      <div className="bg-gray-50 dark:bg-[#0f172a] border-t border-gray-200 dark:border-gray-700 px-6 py-3">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Showing {records.length} {records.length === 1 ? 'record' : 'records'}
        </p>
      </div>
    </div>
  );
};
